import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createCompetition, Discipline } from '../firebase/competitionService';

// ─── helpers ──────────────────────────────────────────────────────────────────

function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD').replace(/\p{Diacritic}/gu, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

// ─── CompetitionSetupPage ─────────────────────────────────────────────────────

const DISCIPLINE_OPTIONS: { id: Discipline; label: string; icon: string; disabled?: boolean }[] = [
  { id: 'boulder', label: 'Boulder', icon: '🪨', disabled: true },
  { id: 'lead',    label: 'Lead / Top-rope', icon: '🧗' },
  { id: 'speed',   label: 'Speed', icon: '⚡', disabled: true },
];

const CompetitionSetupPage: React.FC = () => {
  const navigate = useNavigate();

  const [name,        setName]        = useState('');
  const [date,        setDate]        = useState('');
  const [location,    setLocation]    = useState('');
  const [disciplines, setDisciplines] = useState<Set<Discipline>>(new Set<Discipline>(['lead']));
  const [inBelgium,   setInBelgium]   = useState(false);
  const [adminSecret, setAdminSecret] = useState('');
  const [customId,    setCustomId]    = useState('');
  const [creating,    setCreating]    = useState(false);
  const [error,       setError]       = useState<string | null>(null);

  const effectiveId = customId.trim() || slugify(`${name} ${date.slice(0, 4)}`);

  const toggleDiscipline = (d: Discipline) => {
    setDisciplines(prev => {
      const next = new Set(prev);
      next.has(d) ? next.delete(d) : next.add(d);
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !date || disciplines.size === 0 || !adminSecret.trim()) {
      setError('Please fill in all required fields, select a discipline, and set a password.');
      return;
    }
    const id = effectiveId;
    if (!id) { setError('Could not generate a competition ID. Please enter a name.'); return; }

    setCreating(true);
    setError(null);
    try {
      await createCompetition({
        id,
        name:        name.trim(),
        date,
        location:    location.trim(),
        disciplines: Array.from(disciplines) as Discipline[],
        adminSecret,
        status:      'planning',
        inBelgium,
      });
      navigate(`/competition/${id}/lead-admin?secret=${adminSecret}`);
    } catch (err) {
      setError(String(err));
    } finally {
      setCreating(false);
    }
  };

  // ── Form ──────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-dark text-white px-4 py-12">
      <div className="max-w-xl mx-auto">
        <h1 className="text-3xl font-bold mb-2">Create a Competition</h1>
        <p className="text-gray-400 mb-8">
          Set up a new competition and get a shareable jury link with built-in access control.
        </p>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic info */}
          <div className="bg-gray-800 rounded-xl p-6 space-y-4">
            <h2 className="font-semibold text-gray-200 uppercase text-xs tracking-wider">Competition Details</h2>

            <label className="block">
              <span className="text-sm text-gray-400 mb-1 block">Competition name *</span>
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="e.g. Czech Open Paraclimbing 2026"
                required
                className="w-full px-4 py-2 rounded-lg bg-gray-700 text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </label>

            <div className="grid grid-cols-2 gap-4">
              <label className="block">
                <span className="text-sm text-gray-400 mb-1 block">Date *</span>
                <input
                  type="date"
                  value={date}
                  onChange={e => setDate(e.target.value)}
                  required
                  className="w-full px-4 py-2 rounded-lg bg-gray-700 text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </label>
              <label className="block">
                <span className="text-sm text-gray-400 mb-1 block">Location</span>
                <input
                  type="text"
                  value={location}
                  onChange={e => setLocation(e.target.value)}
                  placeholder="e.g. Prague, CZE"
                  className="w-full px-4 py-2 rounded-lg bg-gray-700 text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </label>
            </div>
          </div>

          {/* Disciplines */}
          <div className="bg-gray-800 rounded-xl p-6">
            <h2 className="font-semibold text-gray-200 uppercase text-xs tracking-wider mb-4">Disciplines *</h2>
            <div className="flex gap-4 flex-wrap">
              {DISCIPLINE_OPTIONS.map(d => (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => !d.disabled && toggleDiscipline(d.id)}
                  disabled={d.disabled}
                  title={d.disabled ? 'Coming soon' : undefined}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 font-semibold text-sm transition-colors ${
                    d.disabled
                      ? 'border-gray-700 bg-gray-700/30 text-gray-600 cursor-not-allowed opacity-50'
                      : disciplines.has(d.id)
                        ? 'border-blue-500 bg-blue-600/20 text-white'
                        : 'border-gray-600 text-gray-400 hover:border-gray-400'
                  }`}
                >
                  <span>{d.icon}</span>
                  {d.label}
                  {d.disabled && <span className="text-xs font-normal">(soon)</span>}
                </button>
              ))}
            </div>
          </div>

          {/* Admin password */}
          <div className="bg-gray-800 rounded-xl p-6 space-y-2">
            <h2 className="font-semibold text-gray-200 uppercase text-xs tracking-wider">Admin Password *</h2>
            <p className="text-xs text-gray-500">Used to access the admin and jury panels. Share only with organisers.</p>
            <input
              type="text"
              value={adminSecret}
              onChange={e => setAdminSecret(e.target.value)}
              placeholder="e.g. wiep2026"
              required
              className="w-full px-4 py-2 rounded-lg bg-gray-700 text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* In Belgium */}
          <div className="bg-gray-800 rounded-xl p-6">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={inBelgium}
                onChange={e => setInBelgium(e.target.checked)}
                className="accent-blue-500 w-4 h-4 mt-0.5 shrink-0"
              />
              <div>
                <span className="text-sm text-gray-200 font-semibold">In Belgium</span>
                <p className="text-xs text-gray-500 mt-0.5">Show the full Belgian website menu on competition pages</p>
              </div>
            </label>
          </div>

          {error && (
            <p className="text-red-400 text-sm bg-red-900/30 border border-red-700 rounded-lg px-4 py-3">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={creating || disciplines.size === 0 || !name.trim() || !date}
            className="w-full py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 rounded-xl font-bold text-lg transition-colors"
          >
            {creating ? 'Creating…' : 'Create Competition →'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default CompetitionSetupPage;
